import type { LiveActionResponse } from '../types';

export type ActionStream = {
  done: LiveActionResponse[];
  next: LiveActionResponse | null;
  upcoming: LiveActionResponse[];
};

/**
 * Partition myActions relative to a countdown clock.
 * Higher roundTimeSecs fire earlier (clock counts down).
 */
export function deriveActionStream(
  actions: LiveActionResponse[],
  currentClockSecs: number,
): ActionStream {
  const sorted = [...actions].sort((a, b) => b.roundTimeSecs - a.roundTimeSecs);
  const done = sorted.filter((a) => a.roundTimeSecs > currentClockSecs);
  const remaining = sorted.filter((a) => a.roundTimeSecs <= currentClockSecs);
  const next = remaining[0] ?? null;
  const upcoming = remaining.slice(1);
  return { done, next, upcoming };
}

/** Seconds until action fires on the countdown clock (0 if already at/past). */
export function secondsUntilAction(action: LiveActionResponse, currentClockSecs: number): number {
  return Math.max(0, currentClockSecs - action.roundTimeSecs);
}

/**
 * Progress through an action window [roundTimeSecs → roundTimeSecs - durationSecs].
 * Returns 0–1, or null when there is no duration.
 */
export function actionWindowProgress(
  action: LiveActionResponse,
  currentClockSecs: number,
): number | null {
  if (action.durationSecs == null || action.durationSecs <= 0) return null;
  const start = action.roundTimeSecs;
  const end = action.roundTimeSecs - action.durationSecs;
  if (currentClockSecs > start) return 0;
  if (currentClockSecs <= end) return 1;
  const elapsed = start - currentClockSecs;
  return Math.min(1, Math.max(0, elapsed / action.durationSecs));
}
